import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import moment, { Moment } from 'moment'
import { Button, Card, Checkbox, Col, DatePicker, Divider, Drawer, Empty, List, Row, Select, Space, Spin, Tag, Tooltip, Typography } from 'antd'
import { CloseOutlined, DislikeFilled, FilePdfOutlined, LikeFilled, StarFilled } from '@ant-design/icons'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useReactToPrint } from 'react-to-print'

import { RootState } from '../../reducers'
import queryKeys from '../../queries/queryKeys'
import {
	fetchSutazOdovzdavaniaNacas,
	ISutazEmployeeResult,
	ISutazRatingItem
} from '../../queries/statistics/fetchStatistics'
import TaskDetailRatingDrawer from '../users/components/TaskDetailRatingDrawer'

const CATEGORY_COLORS = {
	NACAS: '#135200',
	V_ROZMEDZI: '#52c41a',
	MESKAJUCE: '#fa8c16',
	NEUPRATANAT: '#cf1322',
	NAPLANOVANE: '#8c8c8c'
}

const CATEGORY_LABELS: Record<'NACAS' | 'V_ROZMEDZI' | 'MESKAJUCE' | 'NEUPRATANAT' | 'NAPLANOVANE', string> = {
	NACAS: 'Načas',
	V_ROZMEDZI: 'V rozmedzí',
	MESKAJUCE: 'Meškajúce',
	NEUPRATANAT: 'Neupratané',
	NAPLANOVANE: 'Naplánované'
}

type CategoryKey = keyof typeof CATEGORY_COLORS

const getCategoryData = (employee: ISutazEmployeeResult, category: CategoryKey) => {
	if (category === 'NACAS') {
		return employee.nacas
	}

	if (category === 'V_ROZMEDZI') {
		return employee.vRozmedzi
	}

	if (category === 'MESKAJUCE') {
		return employee.meskajuce
	}

	if (category === 'NAPLANOVANE') {
		return employee.naplanovane
	}

	return employee.neupratanat
}


const categoryItems = (employee: ISutazEmployeeResult) => (
	(['NACAS', 'V_ROZMEDZI', 'MESKAJUCE', 'NEUPRATANAT', 'NAPLANOVANE'] as CategoryKey[]).map((category) => ({
		category,
		...getCategoryData(employee, category)
	}))
)

const SutazOdovzdavaniaNacas = () => {
	const { t } = useTranslation('paths')

	const organizations = useSelector((state: RootState) => {
		const profile = state.profile.profile.data
		const allOrgs = [profile?.currentOrganization, ...(profile?.availableOrganizations || [])]
			.filter((org): org is { id: number; name: string } => Boolean(org?.id && org?.name))

		return allOrgs.filter((org, index, source) => index === source.findIndex((item) => item.id === org.id))
	})

	const defaultOrgIds = useMemo(() => organizations.map((organization) => organization.id), [organizations])

	const [selectedOrganizationIds, setSelectedOrganizationIds] = useState<number[]>([])
	const [debouncedOrganizationIds, setDebouncedOrganizationIds] = useState<number[]>([])
	const [selectedUserId, setSelectedUserId] = useState<number | null>(null)
	const [selectedMonth, setSelectedMonth] = useState<string>(moment().format('YYYY-MM'))
	const [sortOrder, setSortOrder] = useState<'best' | 'worst' | 'name'>('name')
	const [drawerOpen, setDrawerOpen] = useState(false)
	const [selectedEmployee, setSelectedEmployee] = useState<ISutazEmployeeResult | null>(null)
	const [drawerActiveCategories, setDrawerActiveCategories] = useState<Set<CategoryKey>>(new Set(['NACAS', 'V_ROZMEDZI', 'MESKAJUCE', 'NEUPRATANAT', 'NAPLANOVANE']))
	const [drawerSort, setDrawerSort] = useState<'name' | 'status' | 'date'>('date')

	const [ratingsDrawerOpen, setRatingsDrawerOpen] = useState(false)
	const [ratingsDrawerStarItems, setRatingsDrawerStarItems] = useState<ISutazRatingItem[]>([])
	const [ratingsDrawerTaskItems, setRatingsDrawerTaskItems] = useState<{ id: number; name: string; count: number }[]>([])
	const [ratingsDrawerTitle, setRatingsDrawerTitle] = useState('')
	const [ratingsDrawerIsGood, setRatingsDrawerIsGood] = useState(true)
	const [ratingsDrawerType, setRatingsDrawerType] = useState<'stars' | 'thumbs'>('stars')

	const printRef = useRef<HTMLDivElement>(null)
	const handlePrint = useReactToPrint({
		content: () => printRef.current,
		documentTitle: `Sútaž-odovzdávanie-${selectedMonth}`
	})

	useEffect(() => {
		if (!selectedOrganizationIds.length && defaultOrgIds.length) {
			setSelectedOrganizationIds(defaultOrgIds)
		}
	}, [defaultOrgIds, selectedOrganizationIds.length])

	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedOrganizationIds(selectedOrganizationIds)
		}, 600)
		return () => clearTimeout(timer)
	}, [selectedOrganizationIds])

	const { data, isLoading } = useQuery({
		queryKey: queryKeys.statistics.sutazOdovzdavaniaNacas({
			organizationIds: debouncedOrganizationIds,
			userId: selectedUserId,
			month: selectedMonth
		}),
		queryFn: () => fetchSutazOdovzdavaniaNacas({
			organizationIds: debouncedOrganizationIds,
			userId: selectedUserId,
			month: selectedMonth
		}),
		enabled: debouncedOrganizationIds.length > 0 && Boolean(selectedMonth)
	})

	const employees = useMemo(() => {
		const raw = data?.data || []
		const scored = (emp: ISutazEmployeeResult) => {
			const actual = (emp.total - emp.naplanovane.count) || 1
			return emp.nacas.count / actual
		}
		if (sortOrder === 'best') {
			return [...raw].sort((a, b) => scored(b) - scored(a))
		}
		if (sortOrder === 'worst') {
			return [...raw].sort((a, b) => scored(a) - scored(b))
		}
		return raw
	}, [data, sortOrder])

	const employeeOptions = (data?.data || []).map((employee) => ({
		value: employee.userId,
		label: employee.fullName
	}))

	const drawerItems = useMemo(() => {
		if (!selectedEmployee) {
			return []
		}

		return categoryItems(selectedEmployee)
			.flatMap((item) => item.items.map((detail) => ({
				...detail,
				category: item.category,
				categoryLabel: CATEGORY_LABELS[item.category]
			})))
			.sort((left, right) => left.expectedDate.localeCompare(right.expectedDate) || left.jobAddress.localeCompare(right.jobAddress))
	}, [selectedEmployee])

	const onOpenEmployee = (employee: ISutazEmployeeResult) => {
		setSelectedEmployee(employee)
		setDrawerOpen(true)
		setDrawerActiveCategories(new Set(['NACAS', 'V_ROZMEDZI', 'MESKAJUCE', 'NEUPRATANAT', 'NAPLANOVANE']))
		setDrawerSort('date')
	}

	const totalScheduled = employees.reduce((sum, employee) => sum + employee.total, 0)
	let reportContent = (
		<div className={'flex direction-col gap-3'}>
			{employees.map((employee) => {
				const total = employee.total || 1
				const categories = categoryItems(employee)

				return (
					<Card
						key={employee.userId}
						size={'small'}
						hoverable
						className={'mb-3'}
						onClick={() => onOpenEmployee(employee)}
					>
						<Row align={'middle'} gutter={[16, 8]}>
							<Col xs={24} md={6}>
								<Typography.Text strong>{employee.fullName}</Typography.Text>
								<br />
								{employee.organizationNames?.length > 0 && (
									<>
										<Typography.Text type={'secondary'} style={{ fontSize: 11 }}>
											{employee.organizationNames.join(', ')}
										</Typography.Text>
										<br />
									</>
								)}
								<Typography.Text type={'secondary'}>
									{employee.total} dokončení
								</Typography.Text>
							</Col>

							<Col xs={24} md={18}>
								<div
									style={{
										display: 'flex',
										width: '100%',
										height: 28,
										borderRadius: 4,
										overflow: 'hidden',
										background: '#f0f0f0'
									}}
								>
									{categories.map((category) => {
										const width = `${(category.count / total) * 100}%`

										if (category.count === 0) {
											return null
										}

										return (
											<Tooltip
												key={category.category}
												title={
													<div>
														<div>
															<strong>{CATEGORY_LABELS[category.category]}: {category.count}</strong>
														</div>
														{category.items.slice(0, 10).map((item) => (
															<div key={`${category.category}-${item.jobId}-${item.expectedDate}`}>{item.jobAddress}</div>
														))}
														{category.items.length > 10 && (
															<div>...a ďalších {category.items.length - 10}</div>
														)}
													</div>
												}
											>
												<div
													style={{
														width,
														background: CATEGORY_COLORS[category.category],
														display: 'flex',
														alignItems: 'center',
														justifyContent: 'center',
														color: '#fff',
														fontSize: 12,
														fontWeight: 600,
														minWidth: category.count > 0 ? 24 : 0
													}}
												>
													{category.count}
												</div>
											</Tooltip>
										)
									})}
								</div>

								{(() => {
									const rbs = employee.ratingsByScore ?? {}
									const badRatingComments = [
										...((rbs[1] ?? { items: [] }).items),
										...((rbs[2] ?? { items: [] }).items)
									].filter((item) => item.comment)
									if (badRatingComments.length === 0) return null
									return (
										<div className={'mt-2'} style={{ borderTop: '1px solid #f0f0f0', paddingTop: 6 }}>
											{badRatingComments.map((item) => (
												<div key={`${employee.userId}-comment-${item.ratingId}`} style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginBottom: 2 }}>
													<StarFilled style={{ color: '#cf1322', fontSize: 11, marginTop: 2, flexShrink: 0 }} />
													<Typography.Text italic style={{ color: '#cf1322', fontSize: 12 }}>
														&bdquo;{item.comment}&ldquo;
													</Typography.Text>
												</div>
											))}
										</div>
									)
								})()}

								{(() => {
									const rbs = employee.ratingsByScore ?? {}
									const s4 = rbs[4] ?? { count: 0, items: [] }
									const s3 = rbs[3] ?? { count: 0, items: [] }
									const s2 = rbs[2] ?? { count: 0, items: [] }
									const s1 = rbs[1] ?? { count: 0, items: [] }
									const good = employee.goodTaskRatings ?? []
									const bad = employee.badTaskRatings ?? []
									const likeCount = good.reduce((sum, t) => sum + t.count, 0)
									const dislikeCount = bad.reduce((sum, t) => sum + t.count, 0)

									const openStars = (items: ISutazRatingItem[], title: string, isGood: boolean, e: React.MouseEvent) => {
										e.stopPropagation()
										if (items.length === 0) return
										setRatingsDrawerStarItems(items)
										setRatingsDrawerTitle(title)
										setRatingsDrawerIsGood(isGood)
										setRatingsDrawerType('stars')
										setRatingsDrawerOpen(true)
									}

									const openThumbs = (tasks: { id: number; name: string; count: number }[], title: string, isGood: boolean, e: React.MouseEvent) => {
										e.stopPropagation()
										if (tasks.length === 0) return
										setRatingsDrawerTaskItems(tasks)
										setRatingsDrawerTitle(title)
										setRatingsDrawerIsGood(isGood)
										setRatingsDrawerType('thumbs')
										setRatingsDrawerOpen(true)
									}

									const starColor = (active: boolean, color: string): string => (active ? color : '#bfbfbf')
									const hasAnyRating = s4.count + s3.count + s2.count + s1.count + likeCount + dislikeCount > 0

									if (!hasAnyRating) return null

									return (
										<div className={'mt-2'} style={{ borderTop: '1px solid #f0f0f0', paddingTop: 8 }}>
											<Row gutter={[8, 4]} wrap>
												<Col>
													<Button size={'small'} disabled={s4.count === 0} onClick={(e) => openStars(s4.items, '4 hviezdičky', true, e)}>
														<StarFilled style={{ color: starColor(s4.count > 0, '#faad14') }} /><StarFilled style={{ color: starColor(s4.count > 0, '#faad14') }} /><StarFilled style={{ color: starColor(s4.count > 0, '#faad14') }} /><StarFilled style={{ color: starColor(s4.count > 0, '#faad14') }} /> {s4.count}
													</Button>
												</Col>
												<Col>
													<Button size={'small'} disabled={s3.count === 0} onClick={(e) => openStars(s3.items, '3 hviezdičky', true, e)}>
														<StarFilled style={{ color: starColor(s3.count > 0, '#faad14') }} /><StarFilled style={{ color: starColor(s3.count > 0, '#faad14') }} /><StarFilled style={{ color: starColor(s3.count > 0, '#faad14') }} /> {s3.count}
													</Button>
												</Col>
												<Col>
													<Button size={'small'} disabled={s2.count === 0} onClick={(e) => openStars(s2.items, '2 hviezdičky', false, e)}>
														<StarFilled style={{ color: starColor(s2.count > 0, '#cf1322') }} /><StarFilled style={{ color: starColor(s2.count > 0, '#cf1322') }} /> {s2.count}
													</Button>
												</Col>
												<Col>
													<Button size={'small'} disabled={s1.count === 0} onClick={(e) => openStars(s1.items, '1 hviezdička', false, e)}>
														<StarFilled style={{ color: starColor(s1.count > 0, '#cf1322') }} /> {s1.count}
													</Button>
												</Col>
												<Col>
													<Button size={'small'} disabled={dislikeCount === 0} onClick={(e) => openThumbs(bad, 'Negatívne hodnotenia', false, e)}>
														<DislikeFilled style={{ color: starColor(dislikeCount > 0, '#dc2626') }} /> {dislikeCount}
													</Button>
												</Col>
												<Col>
													<Button size={'small'} disabled={likeCount === 0} onClick={(e) => openThumbs(good, 'Pozitívne hodnotenia', true, e)}>
														<LikeFilled style={{ color: starColor(likeCount > 0, '#15803d') }} /> {likeCount}
													</Button>
												</Col>
											</Row>
										</div>
									)
								})()}
							</Col>
						</Row>
					</Card>
				)
			})}
		</div>
	)

	if (selectedOrganizationIds.length === 0) {
		reportContent = <Empty description={'Vyber aspoň jednu organizáciu.'} />
	} else if (employees.length === 0) {
		reportContent = <Empty description={'Pre zvolené filtre sa nenašli žiadne dáta.'} />
	}

	return (
		<div className={'page-wrapper'}>
			<Spin spinning={isLoading}>
				<Col className={'grid mt-4'}>
					<div className={'flex direction-col justify-start main-content'}>
						<div className={'flex'} style={{ justifyContent: 'space-between', alignItems: 'center' }}>
							<h3 className={'ml-2'}>{t('paths:appSuperAdminSutaz|title')}</h3>
							<Button
								icon={<FilePdfOutlined />}
								onClick={handlePrint}
								disabled={employees.length === 0}
							>
								Export PDF
							</Button>
						</div>

						<Card className={'mt-4'} bordered={false}>
							<Row gutter={[16, 16]} align={'middle'}>
								<Col xs={24} md={8}>
									<label className={'block mb-1 font-semibold'}>Organizácie</label>
									<Select
										mode={'multiple'}
										value={selectedOrganizationIds}
										onChange={(value) => setSelectedOrganizationIds(value)}
										style={{ width: '100%' }}
										placeholder={'Vyber organizácie'}
										maxTagCount={4}
										allowClear={false}
									>
										{organizations.map((organization) => (
											<Select.Option key={organization.id} value={organization.id}>
												{organization.name}
											</Select.Option>
										))}
									</Select>
								</Col>
								<Col xs={24} md={6}>
									<label className={'block mb-1 font-semibold'}>Zamestnanec</label>
									<Select
										value={selectedUserId}
										onChange={(value) => setSelectedUserId(value ?? null)}
										style={{ width: '100%' }}
										placeholder={'Všetci zamestnanci'}
										allowClear
										options={employeeOptions}
									/>
								</Col>
								<Col xs={24} md={5}>
									<label className={'block mb-1 font-semibold'}>Mesiac</label>
									<DatePicker
										picker={'month'}
										allowClear={false}
										value={moment(selectedMonth, 'YYYY-MM') as Moment}
										onChange={(value) => {
											if (value) {
												setSelectedMonth(moment(value).format('YYYY-MM'))
											}
										}}
										style={{ width: '100%' }}
									/>
								</Col>
								<Col xs={24} md={5}>
									<label className={'block mb-1 font-semibold'}>Zoradiť</label>
									<Select
										value={sortOrder}
										onChange={(value) => setSortOrder(value)}
										style={{ width: '100%' }}
										options={[
											{ value: 'name', label: 'Podľa mena' },
											{ value: 'best', label: '🏆 Najlepší najskôr' },
											{ value: 'worst', label: '⚠️ Najhorší najskôr' }
										]}
									/>
								</Col>
							</Row>
						</Card>

						<div ref={printRef}>
							<Row gutter={[16, 16]} className={'mt-4'}>
								<Col xs={24} md={8}>
									<Card bordered={false}>
										<Typography.Text type={'secondary'}>Zamestnanci</Typography.Text>
										<div className={'text-2xl font-bold'}>{data?.context?.totalEmployees || 0}</div>
									</Card>
								</Col>
								<Col xs={24} md={8}>
									<Card bordered={false}>
										<Typography.Text type={'secondary'}>Naplánované dokončenia</Typography.Text>
										<div className={'text-2xl font-bold'}>{totalScheduled}</div>
									</Card>
								</Col>
								<Col xs={24} md={8}>
									<Card bordered={false}>
										<Typography.Text type={'secondary'}>Filtrovaný mesiac</Typography.Text>
										<div className={'text-2xl font-bold'}>{moment(selectedMonth, 'YYYY-MM').format('MM/YYYY')}</div>
									</Card>
								</Col>
							</Row>

							<Card className={'mt-4'} bordered={false}>
								<Row gutter={[8, 8]} className={'mb-4'}>
									{(['NACAS', 'V_ROZMEDZI', 'MESKAJUCE', 'NEUPRATANAT', 'NAPLANOVANE'] as CategoryKey[]).map((category) => {
										const total = employees.reduce((sum, emp) => sum + getCategoryData(emp, category).count, 0)
										if (total === 0) return null
										return (
											<Col key={category}>
												<Tag color={CATEGORY_COLORS[category]}>{CATEGORY_LABELS[category]}: {total}</Tag>
											</Col>
										)
									})}
								</Row>

								{reportContent}
							</Card>
						</div>
					</div>
				</Col>

				<Drawer
					visible={drawerOpen}
					onClose={() => {
						setDrawerOpen(false)
						setSelectedEmployee(null)
					}}
					title={selectedEmployee?.fullName || 'Detail zamestnanca'}
					width={720}
				>
					{selectedEmployee == null ? (
						<Empty description={'Nie je vybraný žiadny zamestnanec.'} />
					) : (
						<>
							<Row gutter={[6, 6]} className={'mb-3'}>
								{categoryItems(selectedEmployee).filter((c) => c.count > 0).map((category) => {
									const active = drawerActiveCategories.has(category.category)
									const color = CATEGORY_COLORS[category.category]
									return (
										<Col key={`drawer-${category.category}`}>
											<div
												onClick={() => {
													setDrawerActiveCategories((prev) => {
														const next = new Set(prev)
														if (active) {
															next.delete(category.category)
														} else {
															next.add(category.category)
														}
														return next
													})
												}}
												style={{
													display: 'inline-flex',
													alignItems: 'center',
													gap: 5,
													padding: '3px 10px',
													borderRadius: 12,
													cursor: 'pointer',
													userSelect: 'none',
													fontSize: 12,
													fontWeight: 600,
													background: active ? color : 'transparent',
													color: active ? '#fff' : color,
													border: `2px solid ${color}`,
													opacity: active ? 1 : 0.6,
													transition: 'all 0.15s'
												}}
											>
												{active && <span style={{ fontSize: 10, lineHeight: 1 }}>✓</span>}
												{CATEGORY_LABELS[category.category]}: {category.count}
											</div>
										</Col>
									)
								})}
							</Row>

							<Row className={'mb-3'}>
								<Col>
									<Select
										value={drawerSort}
										onChange={setDrawerSort}
										size={'small'}
										style={{ width: 180 }}
										options={[
											{ value: 'date', label: 'Zoradiť: podľa dátumu' },
											{ value: 'name', label: 'Zoradiť: podľa názvu' },
											{ value: 'status', label: 'Zoradiť: podľa statusu' }
										]}
									/>
								</Col>
							</Row>

							{(() => {
								const STATUS_ORDER: Record<CategoryKey, number> = { NACAS: 0, V_ROZMEDZI: 1, MESKAJUCE: 2, NEUPRATANAT: 3, NAPLANOVANE: 4 }
								const filtered = drawerItems.filter((item) => drawerActiveCategories.has(item.category as CategoryKey))
								const sorted = [...filtered].sort((a, b) => {
									if (drawerSort === 'name') return a.jobAddress.localeCompare(b.jobAddress)
									if (drawerSort === 'status') return STATUS_ORDER[a.category as CategoryKey] - STATUS_ORDER[b.category as CategoryKey]
									return a.expectedDate.localeCompare(b.expectedDate) || a.jobAddress.localeCompare(b.jobAddress)
								})
								if (sorted.length === 0) {
									return <Empty description={'Žiadne záznamy pre vybrané filtre.'} />
								}
								return (
									<div className={'flex direction-col gap-2'}>
										{sorted.map((item) => (
											<Card
												key={`${item.category}-${item.jobId}-${item.expectedDate}-${item.finishId || 'missing'}`}
												size={'small'}
												className={'mb-2'}
											>
												<Row gutter={[12, 8]}>
													<Col xs={24} md={10}>
														<Typography.Text strong>{item.jobAddress}</Typography.Text>
													</Col>
													<Col xs={24} md={7}>
														<Typography.Text type={'secondary'}>Plán:</Typography.Text>
														<div>{moment(item.expectedDate).format('DD.MM.YYYY')}</div>
													</Col>
													<Col xs={24} md={7}>
														<Typography.Text type={'secondary'}>Skutočnosť:</Typography.Text>
														<div>{item.actualDate ? moment(item.actualDate).format('DD.MM.YYYY') : '-'}</div>
													</Col>
													<Col xs={24} md={8}>
														<Tag color={CATEGORY_COLORS[item.category]}>{item.categoryLabel}</Tag>
													</Col>
													<Col xs={24} md={8}>
														<Typography.Text type={'secondary'}>Hodnotenie:</Typography.Text>
														<div>
															{item.rating != null ? Array.from({ length: item.rating }).map((_, i) => (
																<StarFilled
																	key={i}
																	style={{ color: '#faad14', marginRight: 2, fontSize: 13 }}
																/>
															)) : '-'}
														</div>
													</Col>
													<Col xs={24} md={8}>
														<Typography.Text type={'secondary'}>Sťažnosť:</Typography.Text>
														<div>
															{item.rating != null && item.rating <= 2 ? (
																<StarFilled style={{ color: '#cf1322' }} />
															) : '-'}
														</div>
													</Col>
													{item.rating != null && item.rating <= 2 && item.ratingComment ? (
														<Col xs={24}>
															<Typography.Text type={'secondary'}>Komentár:</Typography.Text>
															<div>
																<Typography.Text italic style={{ color: '#cf1322' }}>
																	&bdquo;{item.ratingComment}&ldquo;
																</Typography.Text>
															</div>
														</Col>
													) : null}
												</Row>
											</Card>
										))}
									</div>
								)
							})()}
						</>
					)}
				</Drawer>

				{ratingsDrawerType === 'thumbs' ? (
					<TaskDetailRatingDrawer
						open={ratingsDrawerOpen}
						onClose={() => setRatingsDrawerOpen(false)}
						isGood={ratingsDrawerIsGood}
						tasks={ratingsDrawerTaskItems}
					/>
				) : (
					<Drawer
						title={
							<Row justify={'space-between'} align={'middle'}>
								<Space>
									<CloseOutlined style={{ color: '#aaa' }} onClick={() => setRatingsDrawerOpen(false)} />
									<Typography.Text strong>Detail hodnotení – {ratingsDrawerTitle}</Typography.Text>
								</Space>
							</Row>
						}
						width={600}
						visible={ratingsDrawerOpen}
						onClose={() => setRatingsDrawerOpen(false)}
						closeIcon={null}
					>
						<List
							dataSource={ratingsDrawerStarItems}
							split
							renderItem={(item) => (
								<List.Item key={item.ratingId}>
									<div style={{ width: '100%' }}>
										<Row justify={'space-between'} align={'middle'} style={{ marginBottom: 4 }}>
											<Space>
												{Array.from({ length: item.rating }).map((_, i) => (
													<StarFilled key={i} style={{ color: item.rating >= 3 ? '#fadb14' : '#cf1322' }} />
												))}
												<Typography.Text type={'secondary'}>
													{moment(item.actualDate).format('DD.MM.YYYY')}
												</Typography.Text>
											</Space>
											<Tag color={'blue'}>{item.jobAddress}</Tag>
										</Row>
										{item.comment && (
											<>
												<Divider style={{ margin: '8px 0' }} />
												<Typography.Text italic type={'secondary'}>&bdquo;{item.comment}&ldquo;</Typography.Text>
											</>
										)}
									</div>
								</List.Item>
							)}
						/>
					</Drawer>
				)}
			</Spin>
		</div>
	)
}

export default SutazOdovzdavaniaNacas
